import React, { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import { TRANSLATIONS } from "@/lib/i18n";

const AppContext = createContext(null);
export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Attach token interceptor SYNCHRONOUSLY at module load, before any component renders.
// This prevents the race condition where children's useEffects fire before the provider's useEffect attaches the interceptor.
let __interceptorAttached = false;
function attachInterceptorsOnce(onUnauthorized) {
  if (__interceptorAttached) return;
  __interceptorAttached = true;
  axios.interceptors.request.use((config) => {
    const tk = localStorage.getItem("mc_token");
    if (tk) config.headers.Authorization = `Bearer ${tk}`;
    return config;
  });
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      if (err?.response?.status === 401) {
        localStorage.removeItem("mc_token");
        localStorage.removeItem("mc_user");
        onUnauthorized && onUnauthorized();
      }
      return Promise.reject(err);
    }
  );
}
// Attach immediately on module import
attachInterceptorsOnce();

export function AppProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem("mc_token"));
  const [user, setUser] = useState(() => {
    const u = localStorage.getItem("mc_user");
    return u ? JSON.parse(u) : null;
  });
  const [lang, setLang] = useState(() => localStorage.getItem("mc_lang") || "fr");
  const [ready, setReady] = useState(false);

  // Refresh user state on app start (handles stale role/hospital_id from old sessions)
  useEffect(() => {
    if (!token) { setReady(true); return; }
    axios.get(`${API}/auth/me`)
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("mc_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("mc_token");
        localStorage.removeItem("mc_user");
        setToken(null); setUser(null);
      })
      .finally(() => setReady(true));
    // eslint-disable-next-line
  }, []);

  useEffect(() => { localStorage.setItem("mc_lang", lang); }, [lang]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    setToken(res.data.token);
    setUser(res.data.user);
    localStorage.setItem("mc_token", res.data.token);
    localStorage.setItem("mc_user", JSON.stringify(res.data.user));
    return res.data;
  };

  const setSession = (newToken, newUser) => {
    setToken(newToken);
    setUser(newUser);
    localStorage.setItem("mc_token", newToken);
    localStorage.setItem("mc_user", JSON.stringify(newUser));
  };

  const logout = () => {
    setToken(null); setUser(null);
    localStorage.removeItem("mc_token");
    localStorage.removeItem("mc_user");
  };

  const t = TRANSLATIONS[lang] || TRANSLATIONS.fr;

  return (
    <AppContext.Provider value={{ token, user, login, logout, setSession, lang, setLang, t, API, ready }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be inside AppProvider");
  return ctx;
};
